import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";

/**
 * R&D-F1 — Policy Bindings admin panel.
 *
 * Lists the firm's policy-code bindings (registry section → enforcement
 * artefact) with strict/advisory mode, verification status and a red
 * "verification overdue" badge past the 183-day (6-month) cadence. MLCO /
 * COLP / director / admin can Verify, Deprecate (with reason) and create new
 * bindings. Auth is enforced server-side; non-eligible users see an error.
 */
const MODE_STYLE = {
  strict: "text-red-300",
  advisory: "text-amber-300",
};

const ARTEFACT_TYPES = [
  "code_path",
  "config_key",
  "feature_flag",
  "mongo_field",
  "prompt_template",
  "workflow_step",
  "dropdown_option",
  "email_template",
  "notification_rule",
  "cron_schedule",
];

export default function PolicyBindingsPanel({ authHeaders }) {
  const [bindings, setBindings] = useState([]);
  const [cadenceDays, setCadenceDays] = useState(183);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actingId, setActingId] = useState(null);

  // Filters.
  const [modeFilter, setModeFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);

  // Create-binding form.
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    policy_id: "",
    policy_version_id: "",
    policy_section_ref: "",
    bound_artefact_type: "workflow_step",
    bound_artefact_ref: "",
    binding_mode: "strict",
    notes: "",
  });

  // Deprecate modal.
  const [deprecating, setDeprecating] = useState(null);
  const [deprecateReason, setDeprecateReason] = useState("");

  const headers = useCallback(() => {
    if (typeof authHeaders === "function") return authHeaders();
    const email =
      sessionStorage.getItem("bonsai_user_id") ||
      localStorage.getItem("bonsai_user_id") ||
      "";
    return { "X-Bonsai-User-Id": email };
  }, [authHeaders]);

  const fetchBindings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (modeFilter) params.mode = modeFilter;
      if (typeFilter) params.artefact_type = typeFilter;
      if (overdueOnly) params.overdue_only = true;
      const res = await axios.get(`${API}/policy/bindings`, {
        headers: headers(),
        params,
      });
      setBindings(Array.isArray(res.data?.bindings) ? res.data.bindings : []);
      if (res.data?.cadence_days) setCadenceDays(res.data.cadence_days);
    } catch (err) {
      setError(errorMessage(err, "request failed"));
    } finally {
      setLoading(false);
    }
  }, [headers, modeFilter, typeFilter, overdueOnly]);

  useEffect(() => {
    fetchBindings();
  }, [fetchBindings]);

  const verify = useCallback(
    async (id) => {
      setActingId(id);
      setError(null);
      try {
        await axios.post(`${API}/policy/bindings/${id}/verify`, {}, { headers: headers() });
        await fetchBindings();
      } catch (err) {
        setError(errorMessage(err, "verify failed"));
      } finally {
        setActingId(null);
      }
    },
    [headers, fetchBindings]
  );

  const submitDeprecate = useCallback(async () => {
    if (!deprecating || !deprecateReason.trim()) return;
    setActingId(deprecating.id);
    setError(null);
    try {
      await axios.post(
        `${API}/policy/bindings/${deprecating.id}/deprecate`,
        { reason: deprecateReason.trim() },
        { headers: headers() }
      );
      setDeprecating(null);
      setDeprecateReason("");
      await fetchBindings();
    } catch (err) {
      setError(errorMessage(err, "deprecate failed"));
    } finally {
      setActingId(null);
    }
  }, [deprecating, deprecateReason, headers, fetchBindings]);

  const submitCreate = useCallback(async () => {
    setError(null);
    try {
      await axios.post(`${API}/policy/bindings`, form, { headers: headers() });
      setShowCreate(false);
      setForm({
        policy_id: "",
        policy_version_id: "",
        policy_section_ref: "",
        bound_artefact_type: "workflow_step",
        bound_artefact_ref: "",
        binding_mode: "strict",
        notes: "",
      });
      await fetchBindings();
    } catch (err) {
      setError(errorMessage(err, "create failed"));
    }
  }, [form, headers, fetchBindings]);

  const overdueCount = useMemo(
    () => bindings.filter((b) => b.verification_overdue).length,
    [bindings]
  );
  // BM-H3582: a new policy version flags the links whose section changed.
  const reverifyCount = useMemo(
    () => bindings.filter((b) => b.needs_reverification).length,
    [bindings]
  );

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section
      data-testid="policy-bindings-panel"
      className="rounded-xl border border-white/[0.07] bg-navy-card p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-100">
          Policy Bindings{" "}
          {overdueCount > 0 && (
            <span
              data-testid="policy-bindings-overdue-badge"
              className="ml-1 inline-flex items-center justify-center rounded-full bg-red-900/60 text-red-200 text-[10px] font-semibold px-1.5 py-0.5"
            >
              {overdueCount} overdue
            </span>
          )}
          {reverifyCount > 0 && (
            <span
              data-testid="policy-bindings-reverify-badge"
              className="ml-1 inline-flex items-center justify-center rounded-full bg-amber-900/60 text-amber-200 text-[10px] font-semibold px-1.5 py-0.5"
            >
              {reverifyCount} to re-verify
            </span>
          )}
        </h2>
        <div className="space-x-2">
          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            data-testid="policy-bindings-add"
            className="px-2.5 py-1 rounded text-xs font-medium border bg-slate-700 text-slate-100 border-slate-600 hover:bg-slate-600"
          >
            {showCreate ? "Cancel" : "Add binding"}
          </button>
          <button
            type="button"
            onClick={fetchBindings}
            disabled={loading}
            data-testid="policy-bindings-refresh"
            className="px-2.5 py-1 rounded text-xs font-medium border bg-slate-700 text-slate-100 border-slate-600 hover:bg-slate-600 disabled:opacity-50"
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <p className="text-xs text-slate-400">
        Registry-section → enforcement-artefact bindings. Strict bindings block
        the bound gate; advisory bindings warn only. MLCO re-verification cadence
        is {cadenceDays} days (6 months).
      </p>

      {/* Filters. */}
      <div className="flex flex-wrap gap-2 text-xs">
        <select
          value={modeFilter}
          onChange={(e) => setModeFilter(e.target.value)}
          data-testid="policy-bindings-filter-mode"
          className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-100"
        >
          <option value="">All modes</option>
          <option value="strict">Strict</option>
          <option value="advisory">Advisory</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          data-testid="policy-bindings-filter-type"
          className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-100"
        >
          <option value="">All artefact types</option>
          {ARTEFACT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-slate-300">
          <input
            type="checkbox"
            checked={overdueOnly}
            onChange={(e) => setOverdueOnly(e.target.checked)}
            data-testid="policy-bindings-filter-overdue"
          />
          Overdue only
        </label>
      </div>

      {showCreate && (
        <div
          data-testid="policy-bindings-create-form"
          className="rounded border border-slate-700/60 bg-slate-950/40 p-3 space-y-2 text-xs"
        >
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="policy_id" value={form.policy_id} onChange={setF("policy_id")}
              className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-100" />
            <input placeholder="policy_version_id" value={form.policy_version_id} onChange={setF("policy_version_id")}
              className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-100" />
            <input placeholder="section_ref (e.g. 5)" value={form.policy_section_ref} onChange={setF("policy_section_ref")}
              className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-100" />
            <input placeholder="artefact_ref (e.g. REG-01)" value={form.bound_artefact_ref} onChange={setF("bound_artefact_ref")}
              className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-100" />
            <select value={form.bound_artefact_type} onChange={setF("bound_artefact_type")}
              className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-100">
              {ARTEFACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={form.binding_mode} onChange={setF("binding_mode")}
              className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-100">
              <option value="strict">strict</option>
              <option value="advisory">advisory</option>
            </select>
          </div>
          <input placeholder="notes (optional)" value={form.notes} onChange={setF("notes")}
            className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-100" />
          <button
            type="button"
            onClick={submitCreate}
            data-testid="policy-bindings-create-submit"
            className="px-2.5 py-1 rounded text-xs font-medium border bg-emerald-800 text-emerald-100 border-emerald-600 hover:bg-emerald-700"
          >
            Create binding
          </button>
        </div>
      )}

      {error ? (
        <div className="rounded-md border border-red-800/50 bg-red-950/30 p-3 text-sm text-red-200">
          <span data-testid="policy-bindings-error">{error}</span>{" "}
          <button type="button" onClick={fetchBindings}
            className="ml-2 px-2 py-0.5 rounded border border-red-700/60 hover:bg-red-900/30">
            Retry
          </button>
        </div>
      ) : loading && bindings.length === 0 ? (
        <div data-testid="policy-bindings-skeleton" className="h-4 w-44 bg-slate-800 rounded animate-pulse" />
      ) : bindings.length === 0 ? (
        <div className="text-sm text-slate-400" data-testid="policy-bindings-empty">
          No policy bindings.
        </div>
      ) : (
        <table className="w-full text-sm text-slate-200" data-testid="policy-bindings-table">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-400">
              <th className="text-left py-1 pr-3">Policy</th>
              <th className="text-left py-1 pr-3">§</th>
              <th className="text-left py-1 pr-3">Artefact</th>
              <th className="text-left py-1 pr-3">Type</th>
              <th className="text-left py-1 pr-3">Mode</th>
              <th className="text-left py-1 pr-3">Last verified</th>
              <th className="text-left py-1 pr-3">Status</th>
              <th className="text-right py-1">Actions</th>
            </tr>
          </thead>
          <tbody>
            {bindings.map((b) => (
              <tr key={b.id} data-testid="policy-bindings-row" className="border-t border-slate-800/60">
                <td className="py-1 pr-3">{b.policy_slug}</td>
                <td className="py-1 pr-3">
                  {b.policy_section_ref}
                  {b.needs_reverification && (
                    <span
                      data-testid="policy-bindings-row-reverify"
                      title={b.reverification_reason || "The policy section changed in a new version"}
                      className="ml-1 inline-flex items-center rounded bg-amber-900/60 text-amber-200 text-[10px] font-semibold px-1 py-0.5"
                    >
                      changed{b.carried_to_version ? ` in ${b.carried_to_version}` : ""}: re-verify
                    </span>
                  )}
                </td>
                <td className="py-1 pr-3 font-mono text-xs">{b.bound_artefact_ref}</td>
                <td className="py-1 pr-3 text-xs">{b.bound_artefact_type}</td>
                <td className={"py-1 pr-3 capitalize " + (MODE_STYLE[b.binding_mode] || "")}>
                  {b.binding_mode}
                </td>
                <td className="py-1 pr-3 text-xs">
                  {b.last_verified_at ? String(b.last_verified_at).slice(0, 10) : "never"}
                  {b.verification_overdue && (
                    <span
                      data-testid="policy-bindings-row-overdue"
                      className="ml-1 inline-flex items-center rounded bg-red-900/60 text-red-200 text-[10px] font-semibold px-1 py-0.5"
                    >
                      overdue
                    </span>
                  )}
                </td>
                <td className="py-1 pr-3 capitalize text-xs">{b.status}</td>
                <td className="py-1 text-right space-x-1">
                  {b.status === "active" && (
                    <>
                      <button
                        type="button"
                        onClick={() => verify(b.id)}
                        disabled={actingId === b.id}
                        data-testid="policy-bindings-verify"
                        className="px-2 py-0.5 rounded text-xs border border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700 disabled:opacity-50"
                      >
                        Verify
                      </button>
                      <button
                        type="button"
                        onClick={() => { setDeprecating(b); setDeprecateReason(""); }}
                        disabled={actingId === b.id}
                        data-testid="policy-bindings-deprecate"
                        className="px-2 py-0.5 rounded text-xs border border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700 disabled:opacity-50"
                      >
                        Deprecate
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {deprecating && (
        <div
          data-testid="policy-bindings-deprecate-modal"
          className="rounded border border-amber-700/60 bg-slate-950/60 p-3 space-y-2 text-xs"
        >
          <p className="text-slate-200">
            Deprecating <span className="font-mono">{deprecating.policy_slug} §{deprecating.policy_section_ref} → {deprecating.bound_artefact_ref}</span>.
            This invalidates related attestations and alerts the MLRO.
          </p>
          <input
            placeholder="reason (required)"
            value={deprecateReason}
            onChange={(e) => setDeprecateReason(e.target.value)}
            data-testid="policy-bindings-deprecate-reason"
            className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-100"
          />
          <div className="space-x-2">
            <button
              type="button"
              onClick={submitDeprecate}
              disabled={!deprecateReason.trim()}
              data-testid="policy-bindings-deprecate-confirm"
              className="px-2.5 py-1 rounded border bg-amber-800 text-amber-100 border-amber-600 hover:bg-amber-700 disabled:opacity-50"
            >
              Confirm deprecate
            </button>
            <button
              type="button"
              onClick={() => { setDeprecating(null); setDeprecateReason(""); }}
              className="px-2.5 py-1 rounded border border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
